import { useEffect, useMemo } from 'react';
import { useRetailMediaAutoplayPositions } from '@expPlatform/embeds/ads/hooks/useRetailMediaAutoplayPositions';
import { useGlobalStore } from '@core/hooks/useGlobalStore';
import { hasFullscreenPlayerLayoutSelector } from '@core/player/model';
import { useEmbedFeedStore } from '@core/widgets/embedFeed/store';
import { filterCurrentInViewport, sortByOtherArray } from '@embed/tools/utils';
/**
 * Helper to resolve next autoplay video from current videos (thumbnails)
 * in viewport and all videos. Prefers featured videos if applicable, otherwise
 * the first non-ad video in viewport
 */
function resolveNextAutoplay(inViewportIds, videos) {
    const viewportIds = sortByOtherArray(filterCurrentInViewport(inViewportIds), videos.map((v) => v.encoded_id));
    const featuredIds = videos
        .filter((v) => !!v.badge)
        .map((v) => v.encoded_id);
    const featured = viewportIds.filter((id) => featuredIds.includes(id));
    return featured.length > 0 ? featured[0] : viewportIds[0];
}
/**
 * Hook used for resolving which video should autoplay inside Grid/Carousel
 * @param props
 */
export const useUpdateGridAutoplay = (props) => {
    const { isPlayingCurrentContent, videos, autoplay } = props;
    const actions = useEmbedFeedStore((state) => state.actions);
    const inViewportIds = useEmbedFeedStore((state) => state.inViewportIds);
    const currentAutoplayId = useEmbedFeedStore((state) => state.autoplayId);
    const pausedAutoplayId = useEmbedFeedStore((state) => state.pausedAutoplayId);
    const hasFullscreenPlayerLayout = useGlobalStore(hasFullscreenPlayerLayoutSelector);
    const autoplayPositions = useRetailMediaAutoplayPositions();
    const isEligibleForAutoplay = useMemo(() => (autoplay || (currentAutoplayId && !isPlayingCurrentContent)) &&
        !hasFullscreenPlayerLayout, [
        autoplay,
        currentAutoplayId,
        hasFullscreenPlayerLayout,
        isPlayingCurrentContent,
    ]);
    const isCurrentAutoplayInViewport = useMemo(() => currentAutoplayId && inViewportIds[currentAutoplayId], [currentAutoplayId, inViewportIds]);
    const isPausedAutoplayInViewport = useMemo(() => pausedAutoplayId && inViewportIds[pausedAutoplayId], [pausedAutoplayId, inViewportIds]);
    const resolvedAutoplayId = useMemo(() => {
        // returning previous autoplay id if
        if (pausedAutoplayId && isPausedAutoplayInViewport) {
            return pausedAutoplayId;
        }
        const hasAds = !!videos.find((video) => !!video.vast_tag);
        // we should use default behavior if no autoplayPositions or no video ads
        if (!autoplayPositions || !hasAds) {
            return resolveNextAutoplay(inViewportIds, videos);
        }
        // we should only autoplay videos in autoplayPositions
        if (autoplayPositions.length > 0) {
            return (autoplayPositions
                ?.map((index) => videos[index])
                // in case there is more there in index in autoplay positions that is higher
                // than number of videos, we should filter undefined
                .filter(Boolean)
                .find((video) => {
                return inViewportIds[video.encoded_id] && video.vast_tag;
            })?.encoded_id);
        }
        // we should autoplay first non ad video in viewport if autoplayPositions = []
        return videos.find((video) => !video.vast_tag && inViewportIds[video.encoded_id])?.encoded_id;
    }, [
        autoplayPositions,
        inViewportIds,
        isPausedAutoplayInViewport,
        pausedAutoplayId,
        videos,
    ]);
    useEffect(() => {
        // we should reset autoplay if grid is no longer eligible to play
        // any thumbnail (e.g. there is a player in fullscreen on the scene).
        if (!isEligibleForAutoplay) {
            actions.pauseAutoplay();
            return;
        }
        // we should not change video if current autoplay is still in viewport
        if (isCurrentAutoplayInViewport) {
            return;
        }
        // we need to delay resolving of autoplay to prevent fetching of multiple video
        // files on every viewport change
        const timeout = setTimeout(() => {
            if (resolvedAutoplayId) {
                actions.setAutoplay(resolvedAutoplayId);
            }
        }, 1000);
        return () => {
            clearTimeout(timeout);
        };
    }, [
        isEligibleForAutoplay,
        isCurrentAutoplayInViewport,
        resolvedAutoplayId,
        actions,
    ]);
};
